// Del Approved Table

import { useState } from "react";
import styles from "./table-common.module.css";
import { ContextMenu } from "../../../common/ContextMenu";
import { buildCanonicalHomePath } from "../../../../services/RouteUtils";
import { useTableSort } from "./useTableSort";
import { SortableHeader } from "./SortableHeader";
import { utcToLocalDateString } from "../../../../utils/dateUtils";

// Del Approved Table
export const DelApprovedTable = ({
  dels,
  onRowSelect,
  onDoubleClick,
  selectedDelId,
  selectedIds = [],
  onToggleSelect,
  onDownloadSingle,
  clearSelectedApprovedIds,
}) => {
  const [contextMenu, setContextMenu] = useState(null);
  const { sortedItems, sortColumn, sortDirection, onSort } = useTableSort(dels);

  const isApproved = (del) =>
    (del.approved && String(del.approved).toLowerCase() === "true") ||
    (del.del_approval_date && del.del_approval_date !== "-");

  const handleContextMenu = (e, del) => {
    e.preventDefault();
    onRowSelect(del);
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      del: del,
    });
  };

  const getDelUrl = (del) => {
    return buildCanonicalHomePath({
      tab: "approvals",
      delId: del.id,
      action: "view",
      open: true,
    });
  };

  const handleOpenInNewTab = (del) => {
    const url = getDelUrl(del);
    window.open(window.location.origin + url, "_blank");
  };

  const handleCopyLink = (del) => {
    const url = window.location.origin + getDelUrl(del);
    navigator.clipboard.writeText(url).then(() => {
      console.log("Link copied to clipboard");
    });
  };

  const getContextMenuOptions = (del) => {
    const options = [
      {
        label: "Open in new tab",
        action: () => handleOpenInNewTab(del),
      },
      {
        label: "Copy link",
        action: () => handleCopyLink(del),
      },
    ];

    // Add PDF download option for approved DELs
    if (isApproved(del) && onDownloadSingle) {
      options.push({
        label: "Download PDF",
        action: () => onDownloadSingle(del.id),
      });
    }

    return options;
  };

  return (
    <div className={styles.delTableContainer} id="approved_table">
      {/* Card View (Mobile & Tablet) */}
      <div className={styles.cardContainer}>
        <div className={styles.cardList}>
          {dels.map((del) => (
            <div
              key={`card-${del.id}`}
              onClick={() => onRowSelect(del)}
              onDoubleClick={() => onDoubleClick(del)}
              onContextMenu={(e) => handleContextMenu(e, del)}
              className={`${styles.card} ${
                del.id === selectedDelId ? styles.selectedCard : ""
              }`}
            >
              <div className={styles.cardHeaderRow}>
                <strong className={styles.statusLabel}>Approved</strong>
                {isApproved(del) && (
                  <button
                    className={styles.cardActionButton}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDownloadSingle?.(del.id);
                    }}
                  >
                    PDF
                  </button>
                )}
              </div>

              <div className={styles.cardContent}>
                <div className={styles.dataRow}>
                  <span className={styles.label}>Creation Date:</span>
                  <span className={styles.value}>{del.del_creation_date}</span>
                </div>

                <div className={styles.dataRow}>
                  <span className={styles.label}>Updated Date:</span>
                  <span className={styles.value}>
                    {utcToLocalDateString(del.updated)}
                  </span>
                </div>

                <div className={styles.dataRow}>
                  <span className={styles.label}>Submitted Date:</span>
                  <span className={styles.value}>{del.del_submit_date}</span>
                </div>

                <div className={styles.dataRow}>
                  <span className={styles.label}>Approved Date:</span>
                  <span className={styles.value}>{del.del_approval_date}</span>
                </div>

                <div className={styles.dataRow}>
                  <span className={styles.label}>DEL Date:</span>
                  <span className={styles.value}>{del.del_date}</span>
                </div>

                <div className={styles.dataRow}>
                  <span className={styles.label}>Client Name:</span>
                  <span className={styles.value}>{del.client_name}</span>
                </div>

                <div className={styles.dataRow}>
                  <span className={styles.label}>Project Number:</span>
                  <span className={styles.value}>
                    {del.project_number !== "-"
                      ? del.project_number
                      : del.project_name_description}
                  </span>
                </div>

                <div className={styles.dataRow}>
                  <span className={styles.label}>Project Name:</span>
                  <span className={styles.value}>{del.project_name}</span>
                </div>

                <div className={styles.dataRow}>
                  <span className={styles.label}>Phase ID:</span>
                  <span className={styles.value}>{del.phase_id}</span>
                </div>

                <div className={styles.dataRow}>
                  <span className={styles.label}>Task ID:</span>
                  <span className={styles.value}>{del.task_id}</span>
                </div>

                <div className={styles.dataRow}>
                  <span className={styles.label}>Project Manager:</span>
                  <span className={styles.value}>
                    {del.project_manager_name}
                  </span>
                </div>

                <div className={styles.dataRow}>
                  <span className={styles.label}>Invoice Manager:</span>
                  <span className={styles.value}>
                    {del.invoice_manager_name}
                  </span>
                </div>

                <div className={styles.dataRow}>
                  <span className={styles.label}>Submitted By:</span>
                  <span className={styles.value}>{del.submitted_by_name}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Table View (Desktop) */}
      <table className={`${styles.table} ${styles.approvedTable}`}>
        <thead>
          <tr>
            <th>
              <input
                type="checkbox"
                onChange={(e) => {
                  if (e.target.checked) {
                    // Select up to 50 approved DELs
                    const approvedDelIds = dels
                      .filter((d) => isApproved(d))
                      .map((d) => d.id)
                      .slice(0, 50); // Limit to 50
                    approvedDelIds.forEach((id) => {
                      if (!selectedIds?.includes(id)) {
                        onToggleSelect?.(id);
                      }
                    });
                  } else {
                    clearSelectedApprovedIds?.();
                  }
                }}
                checked={
                  dels.filter((d) => isApproved(d)).length > 0 &&
                  dels
                    .filter((d) => isApproved(d))
                    .slice(0, 50)
                    .every((d) => selectedIds?.includes(d.id))
                }
                indeterminate={
                  dels
                    .filter((d) => isApproved(d))
                    .slice(0, 50)
                    .some((d) => selectedIds?.includes(d.id)) &&
                  !dels
                    .filter((d) => isApproved(d))
                    .slice(0, 50)
                    .every((d) => selectedIds?.includes(d.id))
                }
              />
              Select All
            </th>
            <SortableHeader label="Creation Date" column="del_creation_date" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
            <SortableHeader label="Updated Date" column="updated" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
            <SortableHeader label="Submitted Date" column="del_submit_date" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
            <SortableHeader label="Approved Date" column="del_approval_date" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
            <SortableHeader label="DEL Date" column="del_date" sortColumn={sortColumn} sortDirection={sortDirection} onSort={onSort} />
            <th>Client Name</th>
            <th>Project Number</th>
            <th>Project Name</th>
            <th>Phase ID</th>
            <th>Task ID</th>
            <th>Project Manager</th>
            <th>Invoice Manager</th>
            <th>Submitted By</th>
          </tr>
        </thead>
        <tbody>
          {sortedItems.map((del) => (
            <tr
              key={`row-${del.id}`}
              onClick={() => onRowSelect(del)}
              onDoubleClick={() => onDoubleClick(del)}
              onContextMenu={(e) => handleContextMenu(e, del)}
              className={del.id === selectedDelId ? styles.selectedRow : ""}
            >
              <td>
                {isApproved(del) ? (
                  <input
                    type="checkbox"
                    checked={selectedIds?.includes(del.id)}
                    onChange={(e) => {
                      e.stopPropagation();
                      onToggleSelect?.(del.id);
                    }}
                  />
                ) : null}
              </td>
              <td title={del.del_creation_date}>{del.del_creation_date}</td>
              <td title={utcToLocalDateString(del.updated)}>
                {utcToLocalDateString(del.updated)}
              </td>
              <td title={del.del_submit_date}>{del.del_submit_date}</td>
              <td title={del.del_approval_date}>{del.del_approval_date}</td>
              <td title={del.del_date}>{del.del_date}</td>
              <td title={del.client_name}>{del.client_name}</td>
              <td
                title={
                  del.project_number !== "-"
                    ? del.project_number
                    : del.project_name_description
                }
              >
                {del.project_number !== "-"
                  ? del.project_number
                  : del.project_name_description}
              </td>
              <td title={del.project_name}>{del.project_name}</td>
              <td title={del.phase_id}>{del.phase_id}</td>
              <td title={del.task_id}>{del.task_id}</td>
              <td title={del.project_manager_name}>
                {del.project_manager_name}
              </td>
              <td title={del.invoice_manager_name}>
                {del.invoice_manager_name}
              </td>
              <td title={del.submitted_by_name}>{del.submitted_by_name}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* Context Menu */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          options={getContextMenuOptions(contextMenu.del)}
          onClose={() => setContextMenu(null)}
        />
      )}
    </div>
  );
};
